import React, { useEffect, useRef, useState } from 'react';
import { FiCrosshair } from 'react-icons/fi';
import { func } from 'prop-types';

import { IconSpinner } from '../../../components';
import { useIntl } from '../../../util/reactIntl';
import { onTriggerError } from '../../../util/toast';

import css from './CenterOnMyLocationButton.module.css';

export const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};

export const getGeolocationErrorMessageId = error => {
  switch (error?.code) {
    case 1:
      return 'SearchMap.currentLocationPermissionDenied';
    case 2:
      return 'SearchMap.currentLocationUnavailable';
    case 3:
      return 'SearchMap.currentLocationTimeout';
    default:
      return 'SearchMap.currentLocationError';
  }
};

const CenterOnMyLocationButton = props => {
  const { onLocationFound } = props;
  const intl = useIntl();
  const [isLocating, setIsLocating] = useState(false);
  const isMountedRef = useRef(true);
  const requestInProgressRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const finishRequest = () => {
    requestInProgressRef.current = false;
    if (isMountedRef.current) {
      setIsLocating(false);
    }
  };

  const showError = messageId => {
    finishRequest();
    if (isMountedRef.current) {
      onTriggerError(intl.formatMessage({ id: messageId }));
    }
  };

  const handleClick = event => {
    event.stopPropagation();

    if (requestInProgressRef.current) {
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      onTriggerError(intl.formatMessage({ id: 'SearchMap.currentLocationUnsupported' }));
      return;
    }

    requestInProgressRef.current = true;
    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        const latitude = position?.coords?.latitude;
        const longitude = position?.coords?.longitude;
        const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);

        if (!hasValidCoordinates) {
          showError('SearchMap.currentLocationUnavailable');
          return;
        }

        if (!isMountedRef.current) {
          requestInProgressRef.current = false;
          return;
        }

        finishRequest();
        onLocationFound({
          lat: latitude,
          lng: longitude,
          accuracy: position.coords.accuracy,
        });
      },
      error => showError(getGeolocationErrorMessageId(error)),
      GEOLOCATION_OPTIONS
    );
  };

  const accessibleLabel = intl.formatMessage({ id: 'SearchMap.centerOnMyLocation' });

  return (
    <div className={css.locationControl}>
      <button
        type="button"
        className={css.locationButton}
        onClick={handleClick}
        disabled={isLocating}
        aria-label={accessibleLabel}
        aria-busy={isLocating}
      >
        {isLocating ? (
          <IconSpinner rootClassName={css.spinner} />
        ) : (
          <FiCrosshair className={css.locationIcon} aria-hidden="true" focusable="false" />
        )}
      </button>
      <span className={css.tooltip} role="tooltip">
        {accessibleLabel}
      </span>
    </div>
  );
};

CenterOnMyLocationButton.propTypes = {
  onLocationFound: func.isRequired,
};

export default CenterOnMyLocationButton;
